/**
 * Access configurator form — type-specific sections + contact + submit.
 * UI aligned with Prelis ConfiguratorForm (labels, swatches, GDPR, submit).
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type {
  AccessAutomationMount,
  AccessCategoryId,
  AccessColorOption,
  AccessGateVariant,
  AccessPhotoCatalogStored,
  AccessRogerSeriesRule,
  BarrierFinish,
  BarrierModel,
  BarrierSupport,
  DockLevellerType,
  DockSealType,
  HighSpeedFrame,
  HighSpeedGlazing,
  IndustrialMotif,
  IndustrialSurface,
  RollerCassette,
  SwingFrame,
  SwingGlazing,
  SwingPattern,
} from "@shared/access-configurator";
import {
  ACCESS_ASSET_BASE,
  accessSwingGlazingSwatchPath,
  getAccessCategory,
  suggestRogerSeries,
  DEFAULT_ACCESS_ROGER_RULES,
  DEFAULT_INDUSTRIAL_MOTIF,
  DEFAULT_INDUSTRIAL_SURFACE,
  DEFAULT_ROLLER_CASSETTE,
  BARRIER_BOOM_DEFAULT_MM,
  BARRIER_BOOM_MAX_MM,
  BARRIER_PREVIEW,
  DEFAULT_BARRIER_FINISH,
  DEFAULT_BARRIER_SUPPORT,
  DOCK_LEVELLER_TYPES,
  DOCK_SEAL_TYPES,
  DEFAULT_HIGH_SPEED_FRAME,
  DEFAULT_HIGH_SPEED_GLAZING,
  DEFAULT_SWING_FRAME,
  DEFAULT_SWING_GLAZING,
  DEFAULT_SWING_PATTERN,
  HIGH_SPEED_CATALOGUE_MAX_MM,
  HIGH_SPEED_FRAMES,
  HIGH_SPEED_GLAZINGS,
  HIGH_SPEED_HARD_MAX_MM,
  SWING_DOUBLE_MAX_MM,
  SWING_GLAZINGS,
  SWING_PATTERNS,
  SWING_SINGLE_MAX_MM,
  accessColorDisplayName,
  barrierModelFromRule,
  clampBarrierBoom,
  clampHighSpeedSize,
  clampSwingSize,
  highSpeedExceedsCatalogue,
  normalizeRalCode,
  normalizeSwingSelection,
  pickAccessLocalizedName,
  resolveIndustrialSurfaceColors,
  swingColorsForPattern,
  swingPaletteCode,
  swingPatternAllowsZinc,
  swingPatternAllowsSingle,
} from "@shared/access-configurator";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { AccessPreviewState } from "./AccessPreview";

export interface AccessFormValues {
  categoryId: AccessCategoryId;
  gateVariant: AccessGateVariant;
  width: number;
  height: number;
  color: string;
  colorHex: string;
  visualVariant: string;
  motif: IndustrialMotif | string;
  surface: IndustrialSurface | string;
  cassette: RollerCassette | string;
  swingPattern: SwingPattern;
  swingFrame: SwingFrame;
  swingGlazing: SwingGlazing;
  highSpeedFrame: HighSpeedFrame;
  highSpeedGlazing: HighSpeedGlazing;
  barrierModel: BarrierModel;
  barrierFinish: BarrierFinish;
  barrierLed: boolean;
  barrierSkirt: boolean;
  barrierSupport: BarrierSupport;
  dockLeveller: boolean;
  dockLevellerType: DockLevellerType;
  dockSeal: boolean;
  dockSealType: DockSealType;
  dockBumpers: boolean;
  dockDoor: boolean;
  bayCount: number;
  automationWanted: boolean;
  automationMount: AccessAutomationMount;
  leafCount: 1 | 2;
  rogerSeries: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  country: string;
  message: string;
  chcemNaFirmu: boolean;
  ico: string;
  obchodneMeno: string;
  gdpr: boolean;
}

export interface AccessFormProps {
  categoryId: AccessCategoryId;
  colors: AccessColorOption[];
  photoCatalog: AccessPhotoCatalogStored;
  rogerRules?: AccessRogerSeriesRule[];
  preview: AccessPreviewState;
  onPreviewChange: (patch: Partial<AccessPreviewState>) => void;
  token?: string;
  onSubmit: (values: AccessFormValues) => Promise<void>;
  submitting?: boolean;
}

const DEFAULT_WIDTH = 2500;
const DEFAULT_HEIGHT = 2250;

const CONTACT_CARD_CLASS = "rounded-md border border-border/60 bg-muted/20 p-4 space-y-4";

function CheckBadge({ overlay }: { overlay?: boolean }) {
  if (!overlay) {
    return (
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--configurator-accent))] text-white text-[11px] font-bold shadow-md shrink-0">
        ✓
      </span>
    );
  }
  return (
    <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[hsl(var(--configurator-accent))] text-white text-[11px] font-bold shadow-md z-20 pointer-events-none">
      ✓
    </span>
  );
}

function SectionLabel({
  title,
  selected,
  required,
}: {
  title: string;
  selected?: string;
  required?: boolean;
}) {
  return (
    <div className="configurator-section-label">
      <span className="text-sm font-medium">{title}</span>
      {selected ? (
        <>
          <span className="configurator-label-separator"> – </span>
          <span className="selected-value">{selected}</span>
        </>
      ) : null}
      {required ? <span className="configurator-asterisk">*</span> : null}
    </div>
  );
}

function ImageSwatch({
  src,
  fallbackHex,
  selected,
  onSelect,
  title,
}: {
  src: string | null;
  fallbackHex?: string;
  selected: boolean;
  onSelect: () => void;
  title?: string;
}) {
  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [src]);
  const showImg = !!src && !imgError;
  return (
    <button
      type="button"
      onClick={onSelect}
      title={title}
      className={`configurator-swatch relative shadow-sm ${selected ? "selected" : ""}`}
      style={{ width: 100, height: 75 }}
    >
      <span className="swatch-content">
        {showImg ? (
          <img
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span
            className="absolute inset-0 block"
            style={{ backgroundColor: fallbackHex ?? "#e5e5e5" }}
          />
        )}
      </span>
      {selected && <CheckBadge overlay />}
    </button>
  );
}

export default function AccessForm({
  categoryId,
  colors,
  photoCatalog,
  rogerRules = DEFAULT_ACCESS_ROGER_RULES,
  preview: _preview,
  onPreviewChange,
  token,
  onSubmit,
  submitting,
}: AccessFormProps) {
  const { t, i18n } = useTranslation();
  const def = getAccessCategory(categoryId)!;

  const industrialMotifs = photoCatalog.industrial.motifs;
  const industrialSurfaces = photoCatalog.industrial.surfaces;
  const rollerCassettes = photoCatalog.roller.cassettes;
  const defaultMotifId = industrialMotifs[0]?.id ?? DEFAULT_INDUSTRIAL_MOTIF;
  const defaultSurfaceId = industrialSurfaces[0]?.id ?? DEFAULT_INDUSTRIAL_SURFACE;
  const defaultCassetteId = rollerCassettes[0]?.id ?? DEFAULT_ROLLER_CASSETTE;

  const [width, setWidth] = useState(categoryId === "barrier" ? BARRIER_BOOM_DEFAULT_MM : DEFAULT_WIDTH);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);
  const [gateVariant, setGateVariant] = useState<AccessGateVariant>(
    categoryId === "swing" ? "double_leaf" : categoryId === "residential_garage" ? "sectional" : "standard"
  );
  const [color, setColor] = useState(colors[0]?.code ?? "");
  const [colorHex, setColorHex] = useState(colors[0]?.colorHex ?? "#F6F6F6");
  const [motif, setMotif] = useState(defaultMotifId);
  const [surface, setSurface] = useState(defaultSurfaceId);
  const [cassette, setCassette] = useState(defaultCassetteId);
  const [swingPattern, setSwingPattern] = useState<SwingPattern>(DEFAULT_SWING_PATTERN);
  const [swingFrame, setSwingFrame] = useState<SwingFrame>(DEFAULT_SWING_FRAME);
  const [swingGlazing, setSwingGlazing] = useState<SwingGlazing>(DEFAULT_SWING_GLAZING);
  const [highSpeedFrame, setHighSpeedFrame] = useState<HighSpeedFrame>(DEFAULT_HIGH_SPEED_FRAME);
  const [highSpeedGlazing, setHighSpeedGlazing] = useState<HighSpeedGlazing>(DEFAULT_HIGH_SPEED_GLAZING);
  const [barrierFinish, setBarrierFinish] = useState<BarrierFinish>(DEFAULT_BARRIER_FINISH);
  const [barrierLed, setBarrierLed] = useState(false);
  const [barrierSkirt, setBarrierSkirt] = useState(false);
  const [barrierSupport, setBarrierSupport] = useState<BarrierSupport>(DEFAULT_BARRIER_SUPPORT);
  const [dockLeveller, setDockLeveller] = useState(false);
  const [dockLevellerType, setDockLevellerType] = useState<DockLevellerType>("advise");
  const [dockSeal, setDockSeal] = useState(false);
  const [dockSealType, setDockSealType] = useState<DockSealType>("advise");
  const [dockBumpers, setDockBumpers] = useState(false);
  const [dockDoor, setDockDoor] = useState(false);
  const [bayCount, setBayCount] = useState(1);
  const [visualVariant, setVisualVariant] = useState("a");
  const [automationWanted, setAutomationWanted] = useState(def.hasAutomation);
  const [automationMount, setAutomationMount] = useState<AccessAutomationMount>("surface");
  const [leafCount, setLeafCount] = useState<1 | 2>(2);
  const [rogerSeries, setRogerSeries] = useState(categoryId === "barrier" ? "WAY/004" : "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState("Slovensko");
  const [message, setMessage] = useState("");
  const [chcemNaFirmu, setChcemNaFirmu] = useState(false);
  const [ico, setIco] = useState("");
  const [obchodneMeno, setObchodneMeno] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lengthM = useMemo(() => Math.max(width, height) / 1000, [width, height]);

  const suggestions = useMemo(() => {
    if (categoryId === "barrier") {
      return suggestRogerSeries({ categoryId, lengthM: width / 1000 }, rogerRules);
    }
    if (!automationWanted || !def.hasAutomation) return [];
    return suggestRogerSeries(
      {
        categoryId,
        lengthM,
        mount: "surface",
        leafCount: undefined,
      },
      rogerRules
    );
  }, [automationWanted, def.hasAutomation, categoryId, lengthM, rogerRules, width]);

  const barrierRule = suggestions.find((s) => s.series === rogerSeries) ?? suggestions[0];
  const barrierModel: BarrierModel = (barrierRule && barrierModelFromRule(barrierRule)) || "way004";
  const barrierInox = BARRIER_PREVIEW[barrierModel].inox;
  const barrierFinishEffective: BarrierFinish =
    barrierInox && barrierFinish === "inox" ? "inox" : "ral9006";

  useEffect(() => {
    if (categoryId !== "barrier") return;
    if (suggestions.length === 0) return;
    if (!suggestions.some((s) => s.series === rogerSeries)) {
      setRogerSeries(suggestions[0].series);
    }
  }, [categoryId, suggestions, rogerSeries]);

  useEffect(() => {
    if (categoryId !== "barrier") return;
    onPreviewChange({
      barrierModel,
      barrierFinish: barrierFinishEffective,
      barrierLed,
      barrierSkirt,
      barrierSupport,
      lengthMm: width,
    });
  }, [
    categoryId,
    barrierModel,
    barrierFinishEffective,
    barrierLed,
    barrierSkirt,
    barrierSupport,
    width,
    onPreviewChange,
  ]);

  useEffect(() => {
    if (categoryId !== "loading_dock") return;
    onPreviewChange({
      dockLeveller,
      dockLevellerType,
      dockSeal,
      dockSealType,
      dockBumpers,
      dockDoor,
    });
  }, [
    categoryId,
    dockLeveller,
    dockLevellerType,
    dockSeal,
    dockSealType,
    dockBumpers,
    dockDoor,
    onPreviewChange,
  ]);

  const syncPreview = (patch: Partial<AccessPreviewState>) => {
    if (patch.gateVariant != null) {
      setGateVariant(patch.gateVariant);
      onPreviewChange({ gateVariant: patch.gateVariant });
    }
    if (patch.colorHex != null || patch.colorCode != null) {
      if (patch.colorHex != null) setColorHex(patch.colorHex);
      onPreviewChange({
        ...(patch.colorHex != null ? { colorHex: patch.colorHex } : {}),
        ...(patch.colorCode != null ? { colorCode: patch.colorCode } : {}),
      });
    }
    if (patch.visualVariant != null) {
      setVisualVariant(patch.visualVariant);
      onPreviewChange({ visualVariant: patch.visualVariant });
    }
    if (patch.motif != null) {
      setMotif(patch.motif);
      onPreviewChange({ motif: patch.motif });
    }
    if (patch.surface != null) {
      setSurface(patch.surface);
      const nextColors = resolveIndustrialSurfaceColors(photoCatalog, patch.surface);
      const keep =
        nextColors.find((c) => c.code === color) ||
        nextColors.find((c) => normalizeRalCode(c.code) === normalizeRalCode(color)) ||
        nextColors[0];
      if (keep) {
        setColor(keep.code);
        setColorHex(keep.colorHex);
        onPreviewChange({
          surface: patch.surface,
          colorCode: keep.code,
          colorHex: keep.colorHex,
        });
      } else {
        onPreviewChange({ surface: patch.surface });
      }
    }
    if (patch.cassette != null) {
      setCassette(patch.cassette);
      onPreviewChange({ cassette: patch.cassette });
    }
    if (patch.swingPattern != null) {
      setSwingPattern(patch.swingPattern);
      onPreviewChange({ swingPattern: patch.swingPattern });
    }
    if (patch.swingFrame != null) {
      setSwingFrame(patch.swingFrame);
      onPreviewChange({ swingFrame: patch.swingFrame });
    }
    if (patch.swingGlazing != null) {
      setSwingGlazing(patch.swingGlazing);
      onPreviewChange({ swingGlazing: patch.swingGlazing });
    }
    if (patch.highSpeedFrame != null) {
      setHighSpeedFrame(patch.highSpeedFrame);
      onPreviewChange({ highSpeedFrame: patch.highSpeedFrame });
    }
    if (patch.highSpeedGlazing != null) {
      setHighSpeedGlazing(patch.highSpeedGlazing);
      onPreviewChange({ highSpeedGlazing: patch.highSpeedGlazing });
    }
  };

  const applySwing = (
    patch: { leaves?: 1 | 2; pattern?: SwingPattern; frame?: SwingFrame; colorCode?: string },
    changed: "leaves" | "pattern" | "frame" | "finish"
  ) => {
    const nextLeaves = patch.leaves ?? leafCount;
    const sel = normalizeSwingSelection(
      {
        leaves: nextLeaves === 1 ? "single" : "double",
        pattern: patch.pattern ?? swingPattern,
        frame: patch.frame ?? swingFrame,
        finish: patch.colorCode ?? color,
      },
      changed
    );
    const size = clampSwingSize(sel.leaves, width, height);
    const paletteCode = swingPaletteCode(sel.finish);
    const swatch =
      swingColorsForPattern(sel.pattern).find((c) => c.code === paletteCode) ??
      swingColorsForPattern(sel.pattern)[0];
    setLeafCount(sel.leaves === "single" ? 1 : 2);
    setGateVariant(sel.leaves === "single" ? "single_leaf" : "double_leaf");
    setSwingPattern(sel.pattern);
    setSwingFrame(sel.frame);
    setWidth(size.width);
    setHeight(size.height);
    if (swatch) {
      setColor(swatch.code);
      setColorHex(swatch.colorHex);
    }
    onPreviewChange({
      gateVariant: sel.leaves === "single" ? "single_leaf" : "double_leaf",
      swingPattern: sel.pattern,
      swingFrame: sel.frame,
      colorCode: swatch?.code ?? paletteCode,
      colorHex: swatch?.colorHex ?? colorHex,
    });
  };

  const changeWidth = (raw: number) => {
    if (categoryId === "swing") {
      const size = clampSwingSize(leafCount === 1 ? "single" : "double", raw, height);
      setWidth(size.width);
      setHeight(size.height);
      return;
    }
    if (categoryId === "high_speed") {
      const size = clampHighSpeedSize(raw, height);
      setWidth(size.width);
      setHeight(size.height);
      return;
    }
    if (categoryId === "barrier") {
      setWidth(clampBarrierBoom(raw));
      return;
    }
    setWidth(raw);
  };

  const changeHeight = (raw: number) => {
    if (categoryId === "swing") {
      const size = clampSwingSize(leafCount === 1 ? "single" : "double", width, raw);
      setWidth(size.width);
      setHeight(size.height);
      return;
    }
    if (categoryId === "high_speed") {
      const size = clampHighSpeedSize(width, raw);
      setWidth(size.width);
      setHeight(size.height);
      return;
    }
    setHeight(raw);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (categoryId === "loading_dock") {
      if (!dockLeveller && !dockSeal && !dockBumpers && !dockDoor) {
        setError(t("accessConfigurator.errorDockItems"));
        return;
      }
      if (!Number.isInteger(bayCount) || bayCount < 1) {
        setError(t("accessConfigurator.errorBayCount"));
        return;
      }
    }
    if (!name.trim() || !email.trim() || !phone.trim() || !city.trim()) {
      setError(t("accessConfigurator.errorContactRequired"));
      return;
    }
    if (!gdpr) {
      setError(t("accessConfigurator.errorGdpr"));
      return;
    }
    if (!token) {
      setError(t("accessConfigurator.errorMissingToken"));
      return;
    }
    try {
      await onSubmit({
        categoryId,
        gateVariant,
        width: categoryId === "loading_dock" ? 0 : width,
        height: categoryId === "barrier" || categoryId === "loading_dock" ? 0 : height,
        color: categoryId === "loading_dock" ? "" : color,
        colorHex: categoryId === "loading_dock" ? "" : colorHex,
        visualVariant:
          categoryId === "swing"
            ? `${leafCount === 1 ? "single" : "double"}-${swingPattern}-${swingFrame}`
            : categoryId === "high_speed"
              ? `${highSpeedFrame}-${highSpeedGlazing}`
              : categoryId === "barrier"
                ? barrierModel
                : visualVariant,
        motif,
        surface,
        cassette,
        swingPattern,
        swingFrame,
        swingGlazing,
        highSpeedFrame,
        highSpeedGlazing,
        barrierModel,
        barrierFinish: barrierFinishEffective,
        barrierLed,
        barrierSkirt,
        barrierSupport,
        dockLeveller,
        dockLevellerType,
        dockSeal,
        dockSealType,
        dockBumpers,
        dockDoor,
        bayCount,
        automationWanted: categoryId === "barrier" ? true : automationWanted,
        automationMount,
        leafCount,
        rogerSeries: categoryId === "loading_dock" ? "" : rogerSeries || suggestions[0]?.series || "",
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        city: city.trim(),
        address: address.trim(),
        country: country.trim(),
        message: message.trim(),
        chcemNaFirmu,
        ico: ico.trim(),
        obchodneMeno: obchodneMeno.trim(),
        gdpr,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const showPhotoStack = def.previewMode === "photo_stack";
  const showLayeredAppearance =
    (def.previewMode === "layered" || showPhotoStack) &&
    categoryId !== "barrier" &&
    categoryId !== "loading_dock";
  const showVariantSwap = def.previewMode === "variant_swap";
  const industrialPalette = resolveIndustrialSurfaceColors(photoCatalog, surface);
  const rollerPalette =
    photoCatalog.roller.colors.length > 0 ? photoCatalog.roller.colors : colors;
  const palette =
    categoryId === "swing"
      ? swingColorsForPattern(swingPattern)
      : categoryId === "industrial"
        ? industrialPalette
        : categoryId === "roller"
          ? rollerPalette
          : colors;
  const selectedColor = palette.find((c) => c.code === color) ?? colors.find((c) => c.code === color);
  const lang = i18n.language || "sk";
  const colorButtonTitle = (c: AccessColorOption) => {
    if (c.code === "zlaty-dub" || c.code === "orech") {
      return t(`accessConfigurator.foils.${c.code}`);
    }
    const key = c.code.replace(/\D/g, "");
    const fallback = accessColorDisplayName(c, lang, c.code);
    return key ? t(`accessConfigurator.ralNames.${key}`, { defaultValue: fallback }) : fallback;
  };
  const ralKey = (selectedColor?.code ?? color).replace(/\D/g, "");
  const colorName =
    selectedColor?.code === "zlaty-dub" || selectedColor?.code === "orech"
      ? t(`accessConfigurator.foils.${selectedColor.code}`)
      : ralKey
        ? t(`accessConfigurator.ralNames.${ralKey}`, {
            defaultValue: accessColorDisplayName(selectedColor, lang, ""),
          })
        : "";
  const colorSelectedLabel = [color, showPhotoStack && colorName ? colorName : ""]
    .filter(Boolean)
    .join(" — ");

  const labelNamed = (id: string, name: Parameters<typeof pickAccessLocalizedName>[0], i18nPrefix: string) => {
    const fromCatalog = pickAccessLocalizedName(name, lang, "");
    if (fromCatalog && fromCatalog !== id) return fromCatalog;
    return t(`${i18nPrefix}.${id}`, { defaultValue: fromCatalog || id });
  };

  const motifLabel = labelNamed(motif, industrialMotifs.find((m) => m.id === motif)?.name, "accessConfigurator.motifs");
  const surfaceLabel = labelNamed(
    surface,
    industrialSurfaces.find((s) => s.id === surface)?.name,
    "accessConfigurator.surfaces"
  );
  const cassetteLabel = labelNamed(
    cassette,
    rollerCassettes.find((c) => c.id === cassette)?.name,
    "accessConfigurator.cassettes"
  );

  const surfaceThumbSrc = (surfaceId: string): { src: string | null; hex: string } => {
    const surfaceColors = resolveIndustrialSurfaceColors(photoCatalog, surfaceId);
    const first = surfaceColors[0];
    const hex = first?.colorHex ?? "#e5e5e5";
    const file = first?.overlayFilename?.trim();
    if (!file) return { src: null, hex };
    return { src: `${ACCESS_ASSET_BASE}/industrial/overlays/${file}`, hex };
  };

  const colorOverlaySrc = (c: AccessColorOption): string | null => {
    const file = c.overlayFilename?.trim();
    if (!file) {
      if (categoryId === "swing") {
        if (c.code === "zlaty-dub" || c.code === "orech") {
          return `${ACCESS_ASSET_BASE}/swing/overlays/swatch-${c.code}.jpg`;
        }
        const ral = normalizeRalCode(c.code);
        if (ral) return `${ACCESS_ASSET_BASE}/swing/overlays/ral${ral}.jpg`;
      }
      return null;
    }
    if (categoryId === "industrial") return `${ACCESS_ASSET_BASE}/industrial/overlays/${file}`;
    if (categoryId === "roller") return `${ACCESS_ASSET_BASE}/roller/overlays/${file}`;
    if (categoryId === "swing") return `${ACCESS_ASSET_BASE}/swing/overlays/${file}`;
    return null;
  };

  return (
    <form
      className="access-configurator-main-form configurator-main-form space-y-4 pt-6"
      onSubmit={handleSubmit}
      noValidate
    >
      {categoryId === "loading_dock" && (
        <div className="space-y-4" data-testid="loading-dock-items">
          <p className="text-xs text-muted-foreground">{t("accessConfigurator.productLine.loading_dock")}</p>
          <p className="text-xs text-muted-foreground">{t("accessConfigurator.loadingDock.colorNote")}</p>
          <DockCheck
            checked={dockLeveller}
            label={t("accessConfigurator.loadingDock.items.leveller")}
            onChange={setDockLeveller}
          >
            <ChoiceRow
              label={t("accessConfigurator.loadingDock.type")}
              value={dockLevellerType}
              options={DOCK_LEVELLER_TYPES.map((id) => ({
                id,
                label:
                  id === "advise"
                    ? t("accessConfigurator.loadingDock.advise")
                    : t(`accessConfigurator.loadingDock.levellerTypes.${id}`),
              }))}
              onChange={(id) => setDockLevellerType(id as DockLevellerType)}
            />
          </DockCheck>
          <DockCheck
            checked={dockSeal}
            label={t("accessConfigurator.loadingDock.items.seal")}
            onChange={setDockSeal}
          >
            <ChoiceRow
              label={t("accessConfigurator.loadingDock.type")}
              value={dockSealType}
              options={DOCK_SEAL_TYPES.map((id) => ({
                id,
                label:
                  id === "advise"
                    ? t("accessConfigurator.loadingDock.advise")
                    : t(`accessConfigurator.loadingDock.sealTypes.${id}`),
              }))}
              onChange={(id) => setDockSealType(id as DockSealType)}
            />
          </DockCheck>
          <DockCheck
            checked={dockBumpers}
            label={t("accessConfigurator.loadingDock.items.bumpers")}
            hint={t("accessConfigurator.loadingDock.bumpersPair")}
            onChange={setDockBumpers}
          />
          <DockCheck
            checked={dockDoor}
            label={t("accessConfigurator.loadingDock.items.door")}
            onChange={setDockDoor}
          />
          <label className="block text-xs">
            <span className="configurator-label-subtle">
              {t("accessConfigurator.loadingDock.bayCount")} <span className="configurator-asterisk">*</span>
            </span>
            <input
              type="number"
              min={1}
              max={99}
              step={1}
              value={bayCount}
              onChange={(e) => setBayCount(Math.trunc(Number(e.target.value)))}
              className="mt-1 w-28 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-xs">
            <span className="configurator-label-subtle">{t("accessConfigurator.loadingDock.note")}</span>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
      )}

      {/* Dimensions — Prelis: per-field labels + input-mm-wrapper */}
      {categoryId !== "loading_dock" && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">
                {categoryId === "barrier"
                  ? t("accessConfigurator.boomLength")
                  : t("configurator.widthShort", "Šírka")}
                <span className="configurator-asterisk">*</span>
              </Label>
              <div className="input-mm-wrapper">
                <input
                  type="number"
                  min={500}
                  max={
                    categoryId === "barrier"
                      ? BARRIER_BOOM_MAX_MM
                      : categoryId === "high_speed"
                        ? HIGH_SPEED_HARD_MAX_MM.width
                        : categoryId === "swing"
                          ? leafCount === 1
                            ? SWING_SINGLE_MAX_MM.width
                            : SWING_DOUBLE_MAX_MM.width
                          : 20000
                  }
                  value={width}
                  onChange={(e) => changeWidth(Number(e.target.value) || 0)}
                  className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                  required
                />
                <span className="input-mm-suffix">mm</span>
              </div>
            </div>
            {categoryId !== "barrier" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">
                  {t("configurator.heightShort", "Výška")}
                  <span className="configurator-asterisk">*</span>
                </Label>
                <div className="input-mm-wrapper">
                  <input
                    type="number"
                    min={500}
                    max={
                      categoryId === "high_speed"
                        ? HIGH_SPEED_HARD_MAX_MM.height
                        : categoryId === "swing"
                          ? SWING_DOUBLE_MAX_MM.height
                          : 20000
                    }
                    value={height}
                    onChange={(e) => changeHeight(Number(e.target.value) || 0)}
                    className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                    required
                  />
                  <span className="input-mm-suffix">mm</span>
                </div>
              </div>
            )}
          </div>
          {categoryId === "swing" && (
            <p className="text-xs text-muted-foreground">{t("accessConfigurator.swingSizeLimit")}</p>
          )}
          {categoryId === "high_speed" && highSpeedExceedsCatalogue(width, height) && (
            <p className="text-xs text-amber-800">{t("accessConfigurator.highSpeedCatalogueWarn")}</p>
          )}
          {categoryId === "high_speed" && (
            <p className="text-xs text-muted-foreground">
              {t("accessConfigurator.highSpeedSizeLimit", { max: HIGH_SPEED_CATALOGUE_MAX_MM })}
            </p>
          )}
        </div>
      )}

      {categoryId === "residential_garage" && (
        <div className="space-y-1.5">
          <SectionLabel
            title={t("accessConfigurator.gateType")}
            selected={t(`accessConfigurator.variants.${gateVariant}`)}
          />
          <ChoiceRow
            label=""
            hideLabel
            value={gateVariant}
            options={(["sectional", "tilt"] as const).map((v) => ({
              id: v,
              label: t(`accessConfigurator.variants.${v}`),
            }))}
            onChange={(id) => syncPreview({ gateVariant: id as AccessGateVariant })}
          />
        </div>
      )}

      {categoryId === "swing" && (
        <>
          <p className="text-xs text-muted-foreground">{t("accessConfigurator.productLine.swing")}</p>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.leafCount")}
              selected={
                leafCount === 1
                  ? t("accessConfigurator.variants.single_leaf")
                  : t("accessConfigurator.variants.double_leaf")
              }
              required
            />
            <div className="flex flex-wrap gap-3">
              {([1, 2] as const).map((n) => {
                const leaves =
                  n === 1 && swingPatternAllowsSingle(swingPattern) ? "single" : "double";
                const frameForThumb =
                  swingFrame === "zn" && !swingPatternAllowsZinc(swingPattern) ? "rf" : swingFrame;
                return (
                  <ImageSwatch
                    key={n}
                    src={`${ACCESS_ASSET_BASE}/swing/base-${leaves}-${swingPattern}-${frameForThumb}.png`}
                    selected={leafCount === n}
                    title={
                      n === 1
                        ? t("accessConfigurator.variants.single_leaf")
                        : t("accessConfigurator.variants.double_leaf")
                    }
                    onSelect={() => applySwing({ leaves: n }, "leaves")}
                  />
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.pattern")}
              selected={t(`accessConfigurator.swingPatterns.${swingPattern}`)}
              required
            />
            <div className="flex flex-wrap gap-3">
              {SWING_PATTERNS.map((id) => {
                const leaves =
                  leafCount === 1 && swingPatternAllowsSingle(id) ? "single" : "double";
                const frameForThumb =
                  swingFrame === "zn" && !swingPatternAllowsZinc(id) ? "rf" : swingFrame;
                return (
                  <ImageSwatch
                    key={id}
                    src={`${ACCESS_ASSET_BASE}/swing/base-${leaves}-${id}-${frameForThumb}.png`}
                    selected={swingPattern === id}
                    title={t(`accessConfigurator.swingPatternShort.${id}`)}
                    onSelect={() => applySwing({ pattern: id }, "pattern")}
                  />
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.frame")}
              selected={t(`accessConfigurator.swingFrames.${swingFrame}`)}
              required
            />
            <div className="flex flex-wrap gap-3">
              {(["zn", "rf"] as const).map((id) => {
                const leaves =
                  leafCount === 1 && swingPatternAllowsSingle(swingPattern) ? "single" : "double";
                const disabled = id === "zn" && !swingPatternAllowsZinc(swingPattern);
                return (
                  <div key={id} className={disabled ? "opacity-40 pointer-events-none" : undefined}>
                    <ImageSwatch
                      src={`${ACCESS_ASSET_BASE}/swing/base-${leaves}-${swingPattern}-${id}.png`}
                      selected={swingFrame === id}
                      title={t(`accessConfigurator.swingFrames.${id}`)}
                      onSelect={() => {
                        if (!disabled) applySwing({ frame: id }, "frame");
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.glazing")}
              selected={t(`accessConfigurator.swingGlazings.${swingGlazing}`)}
            />
            <div className="flex flex-wrap gap-3">
              {SWING_GLAZINGS.map((id) => (
                <ImageSwatch
                  key={id}
                  src={accessSwingGlazingSwatchPath(id)}
                  selected={swingGlazing === id}
                  title={t(`accessConfigurator.swingGlazings.${id}`)}
                  onSelect={() => syncPreview({ swingGlazing: id })}
                />
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t("accessConfigurator.swingRulesNote")}</p>
        </>
      )}

      {categoryId === "industrial" && (
        <>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel title={t("accessConfigurator.motif")} selected={motifLabel} required />
            <div
              className="grid gap-3"
              style={{ gridTemplateColumns: `repeat(${Math.min(industrialMotifs.length, 3)}, 100px)` }}
            >
              {industrialMotifs.map((m) => {
                const file = m.baseFilename?.trim() || `base-${m.id}.png`;
                return (
                  <ImageSwatch
                    key={m.id}
                    src={`${ACCESS_ASSET_BASE}/industrial/${file}`}
                    selected={motif === m.id}
                    onSelect={() => syncPreview({ motif: m.id })}
                    title={labelNamed(m.id, m.name, "accessConfigurator.motifs")}
                  />
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel title={t("accessConfigurator.surface")} selected={surfaceLabel} required />
            <div
              className="grid gap-3"
              style={{
                gridTemplateColumns: `repeat(${Math.min(industrialSurfaces.length, 3)}, 100px)`,
              }}
            >
              {industrialSurfaces.map((s) => {
                const thumb = surfaceThumbSrc(s.id);
                return (
                  <ImageSwatch
                    key={s.id}
                    src={thumb.src}
                    fallbackHex={thumb.hex}
                    selected={surface === s.id}
                    onSelect={() => syncPreview({ surface: s.id })}
                    title={labelNamed(s.id, s.name, "accessConfigurator.surfaces")}
                  />
                );
              })}
            </div>
          </div>
        </>
      )}

      {categoryId === "roller" && (
        <div className="space-y-1.5 configurator-section-animate">
          <SectionLabel title={t("accessConfigurator.cassette")} selected={cassetteLabel} required />
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: `repeat(${Math.min(rollerCassettes.length, 3)}, 100px)` }}
          >
            {rollerCassettes.map((c) => {
              const file = c.baseFilename?.trim() || `base-${c.id}.png`;
              return (
                <ImageSwatch
                  key={c.id}
                  src={`${ACCESS_ASSET_BASE}/roller/${file}`}
                  selected={cassette === c.id}
                  onSelect={() => syncPreview({ cassette: c.id })}
                  title={labelNamed(c.id, c.name, "accessConfigurator.cassettes")}
                />
              );
            })}
          </div>
        </div>
      )}

      {categoryId === "high_speed" && (
        <>
          <p className="text-xs text-muted-foreground -mt-2">{t("accessConfigurator.productLine.high_speed")}</p>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.frame")}
              selected={t(`accessConfigurator.highSpeedFrames.${highSpeedFrame}`)}
              required
            />
            <div className="flex flex-wrap gap-3">
              {HIGH_SPEED_FRAMES.map((id) => (
                <ImageSwatch
                  key={id}
                  src={`${ACCESS_ASSET_BASE}/high-speed/base-${id}-${highSpeedGlazing}.png`}
                  selected={highSpeedFrame === id}
                  title={t(`accessConfigurator.highSpeedFrames.${id}`)}
                  onSelect={() => syncPreview({ highSpeedFrame: id })}
                />
              ))}
            </div>
          </div>
          <div className="space-y-1.5 configurator-section-animate">
            <SectionLabel
              title={t("accessConfigurator.glazing")}
              selected={t(`accessConfigurator.highSpeedGlazings.${highSpeedGlazing}`)}
              required
            />
            <div className="flex flex-wrap gap-3">
              {HIGH_SPEED_GLAZINGS.map((id) => (
                <ImageSwatch
                  key={id}
                  src={`${ACCESS_ASSET_BASE}/high-speed/base-${highSpeedFrame}-${id}.png`}
                  selected={highSpeedGlazing === id}
                  title={t(`accessConfigurator.highSpeedGlazings.${id}`)}
                  onSelect={() => syncPreview({ highSpeedGlazing: id })}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {showLayeredAppearance && (
        <div className="space-y-1.5 configurator-section-animate">
          <SectionLabel
            title={t("configurator.colorShort", t("accessConfigurator.color"))}
            selected={colorSelectedLabel}
            required
          />
          <div className="grid grid-cols-[100px_100px_100px] gap-3">
            {palette.map((c) => (
              <ImageSwatch
                key={c.code}
                src={colorOverlaySrc(c)}
                fallbackHex={c.colorHex}
                selected={color === c.code}
                title={colorButtonTitle(c)}
                onSelect={() => {
                  if (categoryId === "swing") {
                    applySwing({ colorCode: c.code }, "finish");
                    return;
                  }
                  setColor(c.code);
                  syncPreview({ colorHex: c.colorHex, colorCode: c.code });
                }}
              />
            ))}
          </div>
          {categoryId === "high_speed" && (
            <p className="mt-2 text-xs text-muted-foreground">{t("accessConfigurator.highSpeedColorNote")}</p>
          )}
        </div>
      )}

      {categoryId === "barrier" && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("accessConfigurator.productLine.barrier")}</p>
          <div className="space-y-1.5">
            <SectionLabel
              title={t("accessConfigurator.barrierModel")}
              selected={rogerSeries || suggestions[0]?.series}
              required
            />
            <div className="space-y-2">
              {suggestions.map((s) => {
                const active = (rogerSeries || suggestions[0]?.series) === s.series;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setRogerSeries(s.series);
                      const model = barrierModelFromRule(s);
                      if (model && !BARRIER_PREVIEW[model].inox) setBarrierFinish("ral9006");
                    }}
                    className={`relative w-full text-left px-3 py-2 rounded-md border border-border text-sm ${
                      active ? "bg-muted/40 font-medium" : ""
                    }`}
                  >
                    <span className="font-semibold">{s.series}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {t(`accessConfigurator.rogerRules.${s.id}`, { defaultValue: s.label })}
                    </span>
                    {active && <CheckBadge overlay />}
                  </button>
                );
              })}
            </div>
          </div>
          {barrierInox && (
            <ChoiceRow
              label={t("accessConfigurator.barrierFinish")}
              selectedLabel={t(`accessConfigurator.barrierFinishes.${barrierFinishEffective}`)}
              value={barrierFinishEffective}
              options={[
                { id: "ral9006", label: t("accessConfigurator.barrierFinishes.ral9006") },
                { id: "inox", label: t("accessConfigurator.barrierFinishes.inox") },
              ]}
              onChange={(id) => setBarrierFinish(id as BarrierFinish)}
            />
          )}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={barrierLed}
              onChange={(e) => setBarrierLed(e.target.checked)}
              className="rounded border-input"
            />
            {t("accessConfigurator.barrierLed")}
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={barrierSkirt}
              onChange={(e) => setBarrierSkirt(e.target.checked)}
              className="rounded border-input"
            />
            {t("accessConfigurator.barrierSkirt")}
          </label>
          <ChoiceRow
            label={t("accessConfigurator.barrierSupport")}
            selectedLabel={t(`accessConfigurator.barrierSupports.${barrierSupport}`)}
            value={barrierSupport}
            options={(["none", "fixed", "mobile"] as const).map((id) => ({
              id,
              label: t(`accessConfigurator.barrierSupports.${id}`),
            }))}
            onChange={(id) => setBarrierSupport(id as BarrierSupport)}
          />
        </div>
      )}

      {showVariantSwap && (
        <div className="space-y-1.5">
          <SectionLabel
            title={t("accessConfigurator.look")}
            selected={t(`accessConfigurator.lookVariants.${visualVariant}`)}
          />
          <ChoiceRow
            label=""
            hideLabel
            value={visualVariant}
            options={(["a", "b", "c"] as const).map((v) => ({
              id: v,
              label: t(`accessConfigurator.lookVariants.${v}`),
            }))}
            onChange={(id) => syncPreview({ visualVariant: id })}
          />
        </div>
      )}

      {/* Elektrický pohon — Prelis checkbox */}
      {def.hasAutomation && (
        <div className="space-y-3">
          <div className="flex items-center gap-2.5 py-2">
            <Checkbox
              id="access-electric"
              className="configurator-electric-checkbox"
              checked={automationWanted}
              onCheckedChange={(c) => setAutomationWanted(!!c)}
            />
            <Label htmlFor="access-electric" className="text-sm font-medium">
              {t("configurator.electricDrive")}
            </Label>
          </div>

          {automationWanted && categoryId === "swing" && (
            <div>
              <p className="text-xs configurator-label-subtle mb-1">{t("accessConfigurator.mountType")}</p>
              <ChoiceRow
                label=""
                hideLabel
                value={automationMount}
                options={(["surface", "articulated", "underground"] as const).map((m) => ({
                  id: m,
                  label: t(`accessConfigurator.mounts.${m}`),
                }))}
                onChange={(id) => setAutomationMount(id as AccessAutomationMount)}
              />
            </div>
          )}

          {automationWanted && suggestions.length > 0 && (
            <div className="space-y-1.5">
              <SectionLabel title={t("accessConfigurator.rogerSuggest")} />
              <div className="space-y-2">
                {suggestions.map((s) => {
                  const active =
                    rogerSeries === s.series || (!rogerSeries && suggestions[0]?.series === s.series);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setRogerSeries(s.series)}
                      className={`relative w-full text-left px-3 py-2 rounded-md border border-border text-sm ${
                        active ? "bg-muted/40 font-medium" : ""
                      }`}
                    >
                      <span className="font-semibold">{s.series}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{s.label}</span>
                      {active && <CheckBadge overlay />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contact — Chcem na firmu first, then card */}
      <section className="space-y-4 pt-6 border-t border-border">
        <div className="flex items-center gap-2.5 pt-1">
          <Checkbox
            id="access-chcem-firmu"
            checked={chcemNaFirmu}
            onCheckedChange={(v) => {
              const on = !!v;
              setChcemNaFirmu(on);
              if (!on) {
                setIco("");
                setObchodneMeno("");
              }
            }}
          />
          <Label htmlFor="access-chcem-firmu" className="text-sm font-medium">
            {t("configurator.chcemNaFirmu")}
          </Label>
        </div>

        {chcemNaFirmu && (
          <div className={CONTACT_CARD_CLASS}>
            <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
              {t("configurator.firemneUdaje", t("accessConfigurator.companyName"))}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-bold">{t("accessConfigurator.ico")}</Label>
                <input
                  value={ico}
                  onChange={(e) => setIco(e.target.value)}
                  className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                  inputMode="numeric"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-bold">{t("accessConfigurator.companyName")}</Label>
                <input
                  value={obchodneMeno}
                  onChange={(e) => setObchodneMeno(e.target.value)}
                  className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                />
              </div>
            </div>
          </div>
        )}

        <div className={CONTACT_CARD_CLASS}>
          <h3 className="configurator-card-section-title text-xs font-medium uppercase tracking-[0.12em]">
            {t("configurator.contactSection")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">
                {t("accessConfigurator.name")} <span className="configurator-asterisk">*</span>
              </Label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">
                {t("accessConfigurator.email")} <span className="configurator-asterisk">*</span>
              </Label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">
                {t("accessConfigurator.phone")} <span className="configurator-asterisk">*</span>
              </Label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-bold">
                {t("accessConfigurator.city")} <span className="configurator-asterisk">*</span>
              </Label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
                required
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-sm font-bold">{t("accessConfigurator.address")}</Label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-sm font-bold">{t("accessConfigurator.country")}</Label>
              <input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base"
              />
            </div>
            {categoryId !== "loading_dock" && (
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-sm font-bold">{t("accessConfigurator.message")}</Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base"
                />
              </div>
            )}
          </div>
        </div>

        <label className="configurator-gdpr-label flex items-start gap-2.5 text-sm font-light leading-snug cursor-pointer">
          <Checkbox
            id="access-gdpr"
            checked={gdpr}
            onCheckedChange={(c) => setGdpr(!!c)}
            className="mt-0.5"
          />
          <span>{t("configurator.gdprConsent")}</span>
        </label>
      </section>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <button
        type="submit"
        disabled={submitting || !gdpr}
        className="configurator-btn-primary w-full px-6 py-3 text-sm font-semibold rounded-md text-white disabled:opacity-60"
      >
        {submitting ? t("accessConfigurator.submitting") : t("accessConfigurator.submit")}
      </button>
    </form>
  );
}

function DockCheck({
  checked,
  label,
  hint,
  onChange,
  children,
}: {
  checked: boolean;
  label: string;
  hint?: string;
  onChange: (checked: boolean) => void;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-input"
        />
        <span>
          {label}
          {hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
        </span>
      </label>
      {checked && children ? <div className="pl-6">{children}</div> : null}
    </div>
  );
}

function ChoiceRow({
  label,
  selectedLabel,
  required,
  hideLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  selectedLabel?: string;
  required?: boolean;
  hideLabel?: boolean;
  value: string;
  options: { id: string; label: string; disabled?: boolean }[];
  onChange: (id: string) => void;
}) {
  const selected = selectedLabel ?? options.find((o) => o.id === value)?.label;
  return (
    <div className="space-y-1.5">
      {!hideLabel && (
        <SectionLabel title={label} selected={selected} required={required} />
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            disabled={opt.disabled}
            onClick={() => onChange(opt.id)}
            className={`relative px-3 py-2 rounded-md text-sm border border-border disabled:opacity-40 disabled:cursor-not-allowed ${
              value === opt.id ? "bg-muted/40 font-medium" : ""
            }`}
          >
            {opt.label}
            {value === opt.id && <CheckBadge overlay />}
          </button>
        ))}
      </div>
    </div>
  );
}
